// src/components/payments/PaymentGateway.js
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  CreditCard, 
  Shield, 
  Check, 
  Lock, 
  ArrowLeft,
  Phone,
  MapPin,
  Mail,
  Eye,
  EyeOff
} from 'lucide-react';

const PaymentGateway = ({ 
  orderDetails = {
    items: [
      {
        id: 1,
        name: "Azure River Dining Table",
        image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=150&h=150&fit=crop",
        price: 3200,
        quantity: 1
      }
    ],
    subtotal: 3200,
    shipping: 0,
    tax: 576,
    total: 3776
  },
  onPaymentSuccess,
  onPaymentCancel
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [showCardNumber, setShowCardNumber] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);

  const [billingInfo, setBillingInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'India'
  });

  const [cardInfo, setCardInfo] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    nameOnCard: ''
  });

  const [upiInfo, setUpiInfo] = useState({
    upiId: ''
  });

  const [errors, setErrors] = useState({});

  const paymentMethods = [
    {
      id: 'card',
      name: 'Credit/Debit Card',
      icon: CreditCard,
      description: 'Visa, MasterCard, American Express',
      popular: true
    },
    {
      id: 'upi',
      name: 'UPI Payment',
      icon: Phone,
      description: 'Google Pay, PhonePe, Paytm',
      popular: true
    },
    {
      id: 'netbanking',
      name: 'Net Banking',
      icon: Shield,
      description: 'All major banks supported',
      popular: false
    },
    {
      id: 'wallet',
      name: 'Digital Wallet',
      icon: Phone,
      description: 'Paytm, Amazon Pay, PhonePe',
      popular: false
    }
  ];

  const handleInputChange = (section, field, value) => {
    if (section === 'billing') {
      setBillingInfo(prev => ({ ...prev, [field]: value }));
    } else if (section === 'card') {
      setCardInfo(prev => ({ ...prev, [field]: value }));
    } else if (section === 'upi') {
      setUpiInfo(prev => ({ ...prev, [field]: value }));
    }
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateBillingInfo = () => {
    const newErrors = {};
    
    if (!billingInfo.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!billingInfo.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!billingInfo.email.trim()) newErrors.email = 'Email is required';
    if (!billingInfo.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!billingInfo.address.trim()) newErrors.address = 'Address is required';
    if (!billingInfo.city.trim()) newErrors.city = 'City is required';
    if (!billingInfo.state.trim()) newErrors.state = 'State is required';
    if (!billingInfo.zipCode.trim()) newErrors.zipCode = 'ZIP code is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePaymentInfo = () => {
    const newErrors = {};
    
    if (paymentMethod === 'card') {
      if (!cardInfo.cardNumber.trim()) newErrors.cardNumber = 'Card number is required';
      if (!cardInfo.expiryDate.trim()) newErrors.expiryDate = 'Expiry date is required';
      if (!cardInfo.cvv.trim()) newErrors.cvv = 'CVV is required';
      if (!cardInfo.nameOnCard.trim()) newErrors.nameOnCard = 'Name on card is required';
    } else if (paymentMethod === 'upi') {
      if (!upiInfo.upiId.trim()) newErrors.upiId = 'UPI ID is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (currentStep === 1 && validateBillingInfo()) {
      setCurrentStep(2);
    } else if (currentStep === 2 && validatePaymentInfo()) {
      setCurrentStep(3);
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handlePayment = async () => {
    setIsProcessing(true);
    
    setTimeout(() => {
      setIsProcessing(false);
      setPaymentComplete(true);
      
      setTimeout(() => {
        if (onPaymentSuccess) {
          onPaymentSuccess();
        } else {
          window.location.href = '/';
        }
      }, 2000);
    }, 3000);
  };

  const formatCardNumber = (value) => {
    return value.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
  };

  if (paymentComplete) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="min-h-screen bg-dark-950 flex items-center justify-center p-4"
      >
        <div className="bg-dark-800/50 backdrop-blur-md rounded-3xl p-12 border border-dark-700 text-center max-w-md w-full">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <Check className="h-10 w-10 text-white" />
          </motion.div>
          
          <h2 className="text-3xl font-bold text-white mb-4">
            Payment Successful!
          </h2>
          <p className="text-dark-300 mb-6">
            Your order has been confirmed. You'll receive a confirmation email shortly.
          </p>
          
          <div className="bg-dark-700/50 rounded-xl p-4 mb-6">
            <div className="text-sm text-dark-400 mb-2">Transaction ID</div>
            <div className="text-accent-400 font-mono">TXN{Date.now()}</div>
          </div>
          
          <button
            onClick={() => window.location.href = '/'}
            className="w-full bg-gradient-to-r from-accent-500 to-accent-600 text-white py-3 rounded-lg font-medium hover:from-accent-600 hover:to-accent-700 transition-all duration-300"
          >
            Continue Shopping
          </button>
        </div>
      </motion.div>
    );
  }

  if (isProcessing) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-dark-950 flex items-center justify-center p-4"
      >
        <div className="bg-dark-800/50 backdrop-blur-md rounded-3xl p-12 border border-dark-700 text-center max-w-md w-full">
          <div className="loading-spinner mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold text-white mb-4">
            Processing Payment...
          </h2>
          <p className="text-dark-300 mb-4">
            Please don't close this window. We're securely processing your payment.
          </p>
          <div className="bg-dark-700/50 rounded-xl p-4">
            <div className="flex items-center justify-center space-x-2">
              <Shield className="h-5 w-5 text-green-500" />
              <span className="text-sm text-dark-300">256-bit SSL Encryption</span>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-950 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => window.location.href = '/products'}
              className="p-2 text-dark-400 hover:text-white hover:bg-dark-800 rounded-lg transition-colors duration-200"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-white">Secure Checkout</h1>
              <p className="text-dark-300">Complete your purchase securely</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 text-green-500">
            <Shield className="h-5 w-5" />
            <span className="text-sm font-medium">SSL Secured</span>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4">
            {[1, 2, 3].map((step, index) => (
              <div key={step} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${
                  currentStep >= step
                    ? 'bg-gradient-to-r from-accent-500 to-accent-600 text-white'
                    : 'bg-dark-700 text-dark-400'
                }`}>
                  {currentStep > step ? <Check className="h-5 w-5" /> : step}
                </div>
                {index < 2 && (
                  <div className={`w-16 h-1 mx-2 rounded-full transition-all duration-300 ${
                    currentStep > step ? 'bg-accent-500' : 'bg-dark-700'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
          
          <div className="flex justify-center mt-4">
            <div className="text-sm text-dark-300">
              {currentStep === 1 && 'Billing Information'}
              {currentStep === 2 && 'Payment Method'}
              {currentStep === 3 && 'Review & Confirm'}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-dark-800/50 backdrop-blur-md rounded-2xl p-8 border border-dark-700">
              
              {/* Step 1: Billing Information */}
              {currentStep === 1 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <h2 className="text-2xl font-bold text-white mb-6">Billing Information</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white font-medium mb-2">First Name *</label>
                      <input
                        type="text"
                        value={billingInfo.firstName}
                        onChange={(e) => handleInputChange('billing', 'firstName', e.target.value)}
                        className={`w-full bg-dark-700 border rounded-lg px-4 py-3 text-white placeholder-dark-400 focus:outline-none transition-colors duration-300 ${
                          errors.firstName ? 'border-red-500' : 'border-dark-600 focus:border-accent-500'
                        }`}
                        placeholder="Enter first name"
                      />
                      {errors.firstName && <p className="text-red-400 text-sm mt-1">{errors.firstName}</p>}
                    </div>

                    <div>
                      <label className="block text-white font-medium mb-2">Last Name *</label>
                      <input
                        type="text"
                        value={billingInfo.lastName}
                        onChange={(e) => handleInputChange('billing', 'lastName', e.target.value)}
                        className={`w-full bg-dark-700 border rounded-lg px-4 py-3 text-white placeholder-dark-400 focus:outline-none transition-colors duration-300 ${
                          errors.lastName ? 'border-red-500' : 'border-dark-600 focus:border-accent-500'
                        }`}
                        placeholder="Enter last name"
                      />
                      {errors.lastName && <p className="text-red-400 text-sm mt-1">{errors.lastName}</p>}
                    </div>

                    <div>
                      <label className="block text-white font-medium mb-2">Email Address *</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-dark-400" />
                        <input
                          type="email"
                          value={billingInfo.email}
                          onChange={(e) => handleInputChange('billing', 'email', e.target.value)}
                          className={`w-full bg-dark-700 border rounded-lg pl-12 pr-4 py-3 text-white placeholder-dark-400 focus:outline-none transition-colors duration-300 ${
                            errors.email ? 'border-red-500' : 'border-dark-600 focus:border-accent-500'
                          }`}
                          placeholder="Enter email address"
                        />
                      </div>
                      {errors.email && <p className="text-red-400 text-sm mt-1">{errors.email}</p>}
                    </div>

                    <div>
                      <label className="block text-white font-medium mb-2">Phone Number *</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-dark-400" />
                        <input
                          type="tel"
                          value={billingInfo.phone}
                          onChange={(e) => handleInputChange('billing', 'phone', e.target.value)}
                          className={`w-full bg-dark-700 border rounded-lg pl-12 pr-4 py-3 text-white placeholder-dark-400 focus:outline-none transition-colors duration-300 ${
                            errors.phone ? 'border-red-500' : 'border-dark-600 focus:border-accent-500'
                          }`}
                          placeholder="Enter phone number"
                        />
                      </div>
                      {errors.phone && <p className="text-red-400 text-sm mt-1">{errors.phone}</p>}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-white font-medium mb-2">Address *</label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-4 h-5 w-5 text-dark-400" />
                        <textarea
                          value={billingInfo.address}
                          onChange={(e) => handleInputChange('billing', 'address', e.target.value)}
                          rows={3}
                          className={`w-full bg-dark-700 border rounded-lg pl-12 pr-4 py-3 text-white placeholder-dark-400 focus:outline-none transition-colors duration-300 resize-none ${
                            errors.address ? 'border-red-500' : 'border-dark-600 focus:border-accent-500'
                          }`}
                          placeholder="Enter full address"
                        />
                      </div>
                      {errors.address && <p className="text-red-400 text-sm mt-1">{errors.address}</p>}
                    </div>

                    <div>
                      <label className="block text-white font-medium mb-2">City *</label>
                      <input
                        type="text"
                        value={billingInfo.city}
                        onChange={(e) => handleInputChange('billing', 'city', e.target.value)}
                        className={`w-full bg-dark-700 border rounded-lg px-4 py-3 text-white placeholder-dark-400 focus:outline-none transition-colors duration-300 ${
                          errors.city ? 'border-red-500' : 'border-dark-600 focus:border-accent-500'
                        }`}
                        placeholder="Enter city"
                      />
                      {errors.city && <p className="text-red-400 text-sm mt-1">{errors.city}</p>}
                    </div>

                    <div>
                      <label className="block text-white font-medium mb-2">State *</label>
                      <input
                        type="text"
                        value={billingInfo.state}
                        onChange={(e) => handleInputChange('billing', 'state', e.target.value)}
                        className={`w-full bg-dark-700 border rounded-lg px-4 py-3 text-white placeholder-dark-400 focus:outline-none transition-colors duration-300 ${
                          errors.state ? 'border-red-500' : 'border-dark-600 focus:border-accent-500'
                        }`}
                        placeholder="Enter state"
                      />
                      {errors.state && <p className="text-red-400 text-sm mt-1">{errors.state}</p>}
                    </div>

                    <div>
                      <label className="block text-white font-medium mb-2">ZIP Code *</label>
                      <input
                        type="text"
                        value={billingInfo.zipCode}
                        onChange={(e) => handleInputChange('billing', 'zipCode', e.target.value)}
                        className={`w-full bg-dark-700 border rounded-lg px-4 py-3 text-white placeholder-dark-400 focus:outline-none transition-colors duration-300 ${
                          errors.zipCode ? 'border-red-500' : 'border-dark-600 focus:border-accent-500'
                        }`}
                        placeholder="Enter ZIP code"
                      />
                      {errors.zipCode && <p className="text-red-400 text-sm mt-1">{errors.zipCode}</p>}
                    </div>

                    <div>
                      <label className="block text-white font-medium mb-2">Country *</label>
                      <select
                        value={billingInfo.country}
                        onChange={(e) => handleInputChange('billing', 'country', e.target.value)}
                        className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 text-white focus:border-accent-500 focus:outline-none"
                      >
                        <option value="India">India</option>
                        <option value="United States">United States</option>
                        <option value="United Kingdom">United Kingdom</option>
                      </select>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Payment Method */}
              {currentStep === 2 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <h2 className="text-2xl font-bold text-white mb-6">Payment Method</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                    {paymentMethods.map((method) => (
                      <button
                        key={method.id}
                        onClick={() => setPaymentMethod(method.id)}
                        className={`p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                          paymentMethod === method.id
                            ? 'border-accent-500 bg-accent-500/10'
                            : 'border-dark-600 hover:border-dark-500'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <method.icon className="h-6 w-6 text-accent-400" />
                            <span className="text-white font-medium">{method.name}</span>
                          </div>
                          {method.popular && (
                            <span className="bg-accent-500 text-white px-2 py-1 rounded-full text-xs">
                              Popular
                            </span>
                          )}
                        </div>
                        <p className="text-dark-300 text-sm">{method.description}</p>
                      </button>
                    ))}
                  </div>

                  {/* Payment Details */}
                  {paymentMethod === 'card' && (
                    <div className="space-y-6">
                      <div>
                        <label className="block text-white font-medium mb-2">Card Number *</label>
                        <div className="relative">
                          <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-dark-400" />
                          <input
                            type={showCardNumber ? 'text' : 'password'}
                            value={cardInfo.cardNumber}
                            onChange={(e) => handleInputChange('card', 'cardNumber', formatCardNumber(e.target.value))}
                            className="w-full bg-dark-700 border border-dark-600 rounded-lg pl-12 pr-12 py-3 text-white placeholder-dark-400 focus:border-accent-500 focus:outline-none"
                            placeholder="1234 5678 9012 3456"
                            maxLength="19"
                          />
                          <button
                            type="button"
                            onClick={() => setShowCardNumber(!showCardNumber)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400 hover:text-white"
                          >
                            {showCardNumber ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-white font-medium mb-2">Expiry Date *</label>
                          <input
                            type="text"
                            value={cardInfo.expiryDate}
                            onChange={(e) => handleInputChange('card', 'expiryDate', e.target.value)}
                            className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-dark-400 focus:border-accent-500 focus:outline-none"
                            placeholder="MM/YY"
                            maxLength="5"
                          />
                        </div>

                        <div>
                          <label className="block text-white font-medium mb-2">CVV *</label>
                          <input
                            type="password"
                            value={cardInfo.cvv}
                            onChange={(e) => handleInputChange('card', 'cvv', e.target.value)}
                            className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-dark-400 focus:border-accent-500 focus:outline-none"
                            placeholder="123"
                            maxLength="4"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-white font-medium mb-2">Name on Card *</label>
                        <input
                          type="text"
                          value={cardInfo.nameOnCard}
                          onChange={(e) => handleInputChange('card', 'nameOnCard', e.target.value)}
                          className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-dark-400 focus:border-accent-500 focus:outline-none"
                          placeholder="Enter name as on card"
                        />
                      </div>
                    </div>
                  )}

                  {paymentMethod === 'upi' && (
                    <div>
                      <label className="block text-white font-medium mb-2">UPI ID *</label>
                      <input
                        type="text"
                        value={upiInfo.upiId}
                        onChange={(e) => handleInputChange('upi', 'upiId', e.target.value)}
                        className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-dark-400 focus:border-accent-500 focus:outline-none"
                        placeholder="your-upi-id@paytm"
                      />
                    </div>
                  )}
                </motion.div>
              )}

              {/* Step 3: Review */}
              {currentStep === 3 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <h2 className="text-2xl font-bold text-white mb-6">Review & Confirm</h2>
                  
                  <div className="bg-dark-700/50 rounded-xl p-6 mb-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Order Summary</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-dark-300">Subtotal:</span>
                        <span className="text-white">₹{orderDetails.subtotal.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-dark-300">Shipping:</span>
                        <span className="text-white">{orderDetails.shipping === 0 ? 'Free' : `₹${orderDetails.shipping.toLocaleString()}`}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-dark-300">Tax:</span>
                        <span className="text-white">₹{orderDetails.tax.toLocaleString()}</span>
                      </div>
                      <div className="border-t border-dark-600 pt-2 flex justify-between text-lg font-bold">
                        <span className="text-white">Total:</span>
                        <span className="text-accent-400">₹{orderDetails.total.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6">
                    <div className="flex items-center space-x-3">
                      <Shield className="h-6 w-6 text-green-500" />
                      <div>
                        <div className="text-green-400 font-medium">Your payment is secure</div>
                        <div className="text-green-300 text-sm">256-bit SSL encryption protects your information</div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8">
                <button
                  onClick={handlePreviousStep}
                  disabled={currentStep === 1}
                  className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                    currentStep === 1
                      ? 'bg-dark-700 text-dark-500 cursor-not-allowed'
                      : 'bg-dark-700 text-white hover:bg-dark-600'
                  }`}
                >
                  Previous
                </button>
                
                {currentStep < 3 ? (
                  <button
                    onClick={handleNextStep}
                    className="px-6 py-3 bg-gradient-to-r from-accent-500 to-accent-600 text-white rounded-lg font-medium hover:from-accent-600 hover:to-accent-700 transition-all duration-300"
                  >
                    Next Step
                  </button>
                ) : (
                  <button
                    onClick={handlePayment}
                    className="px-8 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-medium hover:from-green-600 hover:to-green-700 transition-all duration-300 flex items-center space-x-2"
                  >
                    <Lock className="h-5 w-5" />
                    <span>Complete Payment</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-dark-800/50 backdrop-blur-md rounded-2xl p-6 border border-dark-700 sticky top-4">
              <h3 className="text-xl font-bold text-white mb-6">Order Summary</h3>
              
              {/* Order Items */}
              <div className="space-y-4 mb-6">
                {orderDetails.items.map((item, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <div className="text-white font-medium line-clamp-1">{item.name}</div>
                      <div className="text-dark-400 text-sm">Qty: {item.quantity}</div>
                    </div>
                    <div className="text-accent-400 font-medium">
                      ₹{(item.price * item.quantity).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>

              {/* Price Breakdown */}
              <div className="border-t border-dark-700 pt-4 space-y-3">
                <div className="flex justify-between text-dark-300">
                  <span>Subtotal</span>
                  <span>₹{orderDetails.subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-dark-300">
                  <span>Shipping</span>
                  <span>{orderDetails.shipping === 0 ? 'Free' : `₹${orderDetails.shipping.toLocaleString()}`}</span>
                </div>
                <div className="flex justify-between text-dark-300">
                  <span>Tax</span>
                  <span>₹{orderDetails.tax.toLocaleString()}</span>
                </div>
                <div className="border-t border-dark-700 pt-3 flex justify-between text-lg font-bold">
                  <span className="text-white">Total</span>
                  <span className="text-accent-400">₹{orderDetails.total.toLocaleString()}</span>
                </div>
              </div>

              {/* Trust Indicators */}
              <div className="mt-6 pt-6 border-t border-dark-700">
                <div className="flex items-center justify-center space-x-4 text-sm text-dark-400">
                  <div className="flex items-center space-x-1">
                    <Shield className="h-4 w-4" />
                    <span>SSL Secure</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Lock className="h-4 w-4" />
                    <span>Encrypted</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentGateway;